import ServicePageLayout from './ServicePageLayout';
import { IMG } from '@/content/site';

export default function EmployeeBenefits() {
  return (
    <ServicePageLayout
      seo={{
        title: 'Employee Benefits & Wellness Programs | KTK Advisors',
        description:
          'Evaluate preventive care, virtual health, and wellness options alongside your current benefits—including potential employer payroll savings where eligibility allows.',
        path: '/solutions/employee-benefits',
      }}
      group="Employee Benefits & Wellness"
      headline="Support your people. Explore a healthier cost structure."
      lede="Benefits are one of your largest recurring investments. KTK Advisors helps you evaluate preventive care and virtual health options, wellness support, and potential employer payroll savings—alongside what you already offer."
      audienceIntro="For employers taking benefits seriously."
      audience={[
        'Small business owners offering benefits for the first time',
        'Growing companies reviewing rising premium costs',
        'Leadership teams evaluating workforce wellness support',
        'Organizations with low utilization of existing benefits',
      ]}
      image={IMG.benefits}
      imageAlt="Open-plan office with natural light where employees work at desks"
      problems={[
        {
          t: 'Premiums that climb every year',
          d: 'Benefit costs rise faster than most line items, and many employers have never had a structured second opinion on the options.',
        },
        {
          t: 'Benefits nobody uses',
          d: 'Paying for benefits employees rarely touch helps no one. Low utilization often means the structure no longer fits the workforce.',
        },
        {
          t: 'Preventive and virtual care left on the table',
          d: 'Access to preventive care and virtual health services can improve how people experience their benefits—when the structure is set up well.',
        },
        {
          t: 'Missed payroll structure opportunities',
          d: 'For eligible employers, certain program structures can create potential employer payroll savings. Eligibility and program structure determine what is actually possible.',
        },
      ]}
      capabilities={[
        'Review of your current benefits structure',
        'Evaluation of preventive care access options',
        'Evaluation of virtual health services',
        'Employee wellness program support',
        'Analysis of potential employer payroll savings where eligibility allows',
        'Coordination of suitable programs',
        'Plain-language explanation of costs and eligibility',
      ]}
      diagram={{
        title: 'Evaluate alongside what you already offer.',
        steps: [
          { n: '01', label: 'Review current benefits & costs' },
          { n: '02', label: 'Identify eligible options' },
          { n: '03', label: 'Compare structure & impact' },
          { n: '04', label: 'Decide with clear terms' },
        ],
      }}
      diagramCaption="This is an evaluation, not a pitch. The goal is a clear comparison of what exists today against what could work better—so leadership can decide with real information."
      note="Availability, costs, eligibility, and savings depend on the specific program and employer circumstances. These programs are evaluated alongside your existing benefits and are not represented as a universal replacement for health insurance."
      helpsIntro="KTK Advisors coordinates the evaluation—so you compare options with clear, business-focused explanations."
      helps={[
        {
          t: 'Start from your workforce',
          d: 'Team size, structure, and current benefits shape what is worth evaluating. We start there, not with a product.',
        },
        {
          t: 'Explain eligibility honestly',
          d: 'Where payroll savings are possible, eligibility and program structure decide the outcome. We explain what applies to you before anything is proposed.',
        },
        {
          t: 'Evaluate alongside existing benefits',
          d: 'You keep what works. The evaluation shows where preventive care, virtual health, and wellness options could strengthen what you offer.',
        },
      ]}
      faqs={[
        {
          q: 'Is this a replacement for health insurance?',
          a: 'No. These are options to evaluate alongside your existing benefits. KTK Advisors does not represent them as a universal replacement for health insurance.',
        },
        {
          q: 'Are the payroll savings guaranteed?',
          a: 'No. Potential employer payroll savings depend on eligibility and program structure. Any figures discussed are specific to your circumstances after a proper review—never fixed assumptions.',
        },
        {
          q: 'What does compliance look like?',
          a: 'Program eligibility and compliance requirements depend on your specific situation. We explain what we know clearly, and terms are reviewed before any engagement begins. You should involve your own legal and tax advisors where appropriate.',
        },
        {
          q: 'What information would we start with?',
          a: 'A conversation about your team and your current benefits. Please do not include sensitive employee records in website inquiries.',
        },
        {
          q: 'What does it cost to evaluate?',
          a: 'The first conversation is a consultation. If a structured evaluation makes sense, the scope and fees are explained before you decide to move forward.',
        },
      ]}
      cta={{
        title: 'Explore your benefits options.',
        copy: 'Bring your current benefits picture and your questions. We\'ll help you evaluate what could work better for your people and your cost structure.',
        button: 'Explore Your Benefits Options',
      }}
      interestParam="employee-benefits"
    />
  );
}
